import { useCallback, useState, type ChangeEvent } from "react";
import { FaRegTrashCan } from "react-icons/fa6";

import { useLoading } from "shared/hooks/use-loading";
import { Button } from "shared/ui/button";
import { ConfirmDialog } from "shared/ui/confirm-dialog";
import { Sheet } from "shared/ui/sheet";

type Props = {
  isOpen: boolean;
  initialValue: string;
  placeholder?: string;
  title?: string;
  onClose: () => void;
  onSave: (value: string) => Promise<void>;
  onDelete?: () => Promise<void>;
};

export const PersistentContextSheet = (props: Props) => {
  const {
    isOpen,
    placeholder,
    title,
    initialValue,
    onClose,
    onDelete: onDeleteProp,
    onSave: onSaveProp,
  } = props;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);

  const [deleteLoading, onDelete] = useLoading(onDeleteProp);
  const [saveLoading, onSave] = useLoading(onSaveProp);

  const refCb = useCallback((node: HTMLTextAreaElement | null) => {
    if (!node) return;
    setTimeout(() => {
      node.focus();
    }, 500);
  }, []);

  const [value, setValue] = useState(initialValue);
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setValue(val);
  };

  const handleClose = () => {
    setIsCloseDialogOpen(false);
    onClose();
  };

  const handleDelete = async () => {
    await onDelete?.();
    setIsDeleteDialogOpen(false);
  };

  return (
    <>
      <Sheet
        className="pointer-events-none"
        isOpen={isOpen}
        onClose={handleClose}
      >
        <Sheet.Header
          onClose={() => {
            if (initialValue !== value) return setIsCloseDialogOpen(true);
            onClose();
          }}
        >
          <h3>{title}</h3>

          <div className="ml-auto flex items-center gap-x-3">
            {onDeleteProp && (
              <Button
                theme="danger"
                size="34"
                rounded
                className="bg-pale-red/15"
                onClick={() => {
                  setIsDeleteDialogOpen(true);
                }}
              >
                <FaRegTrashCan className="text-pale-red size-5" />
              </Button>
            )}

            <Button
              isLoading={saveLoading}
              size="34"
              onClick={() => onSave(value)}
            >
              Save
            </Button>
          </div>
        </Sheet.Header>
        <Sheet.Content>
          <textarea
            ref={refCb}
            className="w-full rounded-xl p-4 outline-hidden transition-colors focus:bg-white/5"
            value={value}
            rows={5}
            onChange={handleChange}
            placeholder={placeholder}
          />
        </Sheet.Content>
      </Sheet>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        isLoading={deleteLoading}
        onOpenChange={setIsDeleteDialogOpen}
        title="Persistent Context"
        description="Are you sure you want to delete this Context? This action cannot be undone."
        cancelText="Back to Editing"
        confirmText="Delete Permanently"
        onCancel={() => {
          setIsDeleteDialogOpen(false);
        }}
        onConfirm={handleDelete}
      />

      <ConfirmDialog
        isOpen={isCloseDialogOpen}
        onOpenChange={setIsCloseDialogOpen}
        title="Persistent Context"
        description={
          initialValue
            ? "You have unsaved changes. Are you sure you want to discard them?"
            : "This Context isn't saved yet. Are you sure you want to cancel its creation?"
        }
        cancelText="Back to Editing"
        confirmText={initialValue ? "Discard changes" : "Cancel Saving"}
        onCancel={() => {
          setIsCloseDialogOpen(false);
        }}
        onConfirm={handleClose}
      />
    </>
  );
};
